/**
 * LiveStreamingStatPill — the shared live app-bar stat pill shell.
 *
 * Web port of the rounded translucent stat pill Flutter renders twice in the
 * live-streaming app bar: `_watchersStats`
 * (`lib/presentation/screens/community/live_streaming_screen.dart:1089-1132`)
 * and `_coinsStats` (`:1134-1176`). The two Flutter widgets are near-identical
 * copies differing only in icon asset, icon tint, and how the value is
 * formatted, so the web keeps ONE shell (`workflow/lessons.md` "reuse beats
 * literal porting") that `LiveStreamingWatchersStat` and `LiveStreamingCoinsStat`
 * both delegate to.
 *
 * Placement + visibility gating live on the page — Flutter shows the stats only
 * when `_firstFrameRendered && !_clearScreen && !_liveStreamingEnded &&
 * !_isOpeningChannel` (`:1079-1084`), so this shell neither re-derives that gate
 * nor positions itself.
 *
 * Theme/responsive + `qaAttr` are mandatory (`presentation.md`). The pill
 * background reuses `theme.button.round` — the same translucent-dark token the
 * sibling `LiveStreamingControls` buttons use over the live-video surface — for
 * visual cohesion (Flutter's native value is `AppColors.backgroundColorLight @
 * .5` = `#171717@50%`, a dark pill with white value text). The icon tint is
 * caller-supplied (`theme.white` for watchers, `theme.exclusive` for coins,
 * matching Flutter `:1111` / `:1156`); the value text is always `theme.white`
 * (`AppStyles.liveStatsTextStyle`). `data-qa-value` carries the raw value so QA
 * can assert it.
 */

import React from 'react';

import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { AssetIcon } from '@/presentation/components/common/icon/AssetIcon';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface LiveStreamingStatPillProps {
  /** Icon asset path (e.g. `/assets/images/eye.png`, `/assets/images/coins.png`). */
  iconPath: string;
  /** Icon tint (Flutter `color:` on the `Image.asset`). */
  iconColor: string;
  /** The already-formatted display value (raw count or compact number). */
  value: React.ReactNode;
  /** `data-qa-id` for the pill root. */
  qaId: string;
  /** `data-qa-value` — the raw (unformatted) value for QA assertions. */
  qaValue: string | number;
}

export const LiveStreamingStatPill: React.FC<LiveStreamingStatPillProps> = ({
  iconPath,
  iconColor,
  value,
  qaId,
  qaValue,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  return (
    <div
      {...qaAttr({ id: qaId, value: qaValue })}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: responsive.liveStreaming.statsGap,
        backgroundColor: theme.button.round,
        borderRadius: responsive.borderRadius.lg,
        padding: `${responsive.liveStreaming.statsPillPaddingVertical}px ${responsive.liveStreaming.statsPillPaddingHorizontal}px`,
      }}
    >
      <AssetIcon
        path={iconPath}
        width={responsive.liveStreaming.statsIconSize}
        height={responsive.liveStreaming.statsIconSize}
        color={iconColor}
      />
      <span
        style={{
          color: theme.white,
          fontSize: responsive.font.sm,
        }}
      >
        {value}
      </span>
    </div>
  );
};
